import React from 'react';
import {
  Card as MuiCard,
  CardContent,
  CardHeader,
  CardActions,
  Typography,
  IconButton,
  Divider,
  Box,
  styled,
} from '@mui/material';
import type { CardProps as MuiCardProps } from '@mui/material';
import { MoreVert, Close } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

// Custom card variants
export type CardVariant = 'elevated' | 'outlined' | 'filled' | 'glass';
export type CardSize = 'small' | 'medium' | 'large';

// Extended props interface
export interface CustomCardProps extends Omit<MuiCardProps, 'variant' | 'title'> {
  variant?: CardVariant;
  size?: CardSize;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  avatar?: React.ReactNode;
  headerAction?: React.ReactNode;
  showMenu?: boolean;
  onMenuClick?: () => void;
  showClose?: boolean;
  onClose?: () => void;
  actions?: React.ReactNode;
  footer?: React.ReactNode;
  loading?: boolean;
  hover?: boolean;
  clickable?: boolean;
  bordered?: boolean;
}

// Styled components
const StyledCard = styled(MuiCard, {
  shouldForwardProp: prop =>
    !(typeof prop === 'string' && prop.startsWith('$')) &&
    prop !== 'size' &&
    prop !== 'loading' &&
    prop !== 'hover' &&
    prop !== 'clickable' &&
    prop !== 'bordered',
})<{
  $variant?: CardVariant;
  size?: CardSize;
  loading?: boolean;
  hover?: boolean;
  clickable?: boolean;
  bordered?: boolean;
}>(({ theme, $variant: cardVariant, size, loading, hover, clickable, bordered }) => {
  const sizeConfig = {
    small: {
      padding: { xs: theme.spacing(1), sm: theme.spacing(1.25) },
      borderRadius: theme.spacing(1),
    },
    medium: {
      padding: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
      borderRadius: theme.spacing(1.5),
    },
    large: {
      padding: { xs: theme.spacing(2), sm: theme.spacing(2.5) },
      borderRadius: theme.spacing(2),
    },
  };

  const currentSize = sizeConfig[size || 'medium'];

  const baseStyles = {
    borderRadius: currentSize.borderRadius,
    transition: 'all 0.3s ease-in-out',
    position: 'relative' as const,
    overflow: 'visible' as const,
    ...(bordered && {
      border: `1px solid ${theme.palette.divider}`,
    }),
    ...(loading && {
      opacity: 0.6,
      pointerEvents: 'none' as const,
    }),
    ...(clickable && {
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[8],
      },
    }),
    ...(hover && {
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[12],
      },
    }),
  };

  // Variant-specific styles
  const variantStyles = {
    elevated: {
      boxShadow: theme.shadows[2],
      backgroundColor: theme.palette.background.paper,
      '&:hover': hover
        ? {
            boxShadow: theme.shadows[8],
          }
        : {},
    },
    outlined: {
      boxShadow: 'none',
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      '&:hover': hover
        ? {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
          }
        : {},
    },
    filled: {
      boxShadow: 'none',
      backgroundColor: theme.palette.grey[50],
      border: `1px solid ${theme.palette.grey[200]}`,
      '&:hover': hover
        ? {
            backgroundColor: theme.palette.grey[100],
            borderColor: theme.palette.grey[300],
          }
        : {},
    },
    glass: {
      backgroundColor: alpha(theme.palette.background.paper, 0.8),
      backdropFilter: 'blur(10px)',
      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
      boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
      '&:hover': hover
        ? {
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }
        : {},
    },
  };

  return {
    ...baseStyles,
    ...variantStyles[cardVariant || 'elevated'],
  };
});

const StyledCardContent = styled(CardContent)<{ size?: CardSize }>(({ theme, size }) => {
  const sizeConfig = {
    small: { 
      padding: { xs: theme.spacing(1), sm: theme.spacing(1.25) }, 
      paddingBottom: { xs: `${theme.spacing(1)} !important`, sm: `${theme.spacing(1.25)} !important` }
    },
    medium: { 
      padding: { xs: theme.spacing(1.5), sm: theme.spacing(2) }, 
      paddingBottom: { xs: `${theme.spacing(1.5)} !important`, sm: `${theme.spacing(2)} !important` }
    },
    large: { 
      padding: { xs: theme.spacing(2), sm: theme.spacing(2.5) }, 
      paddingBottom: { xs: `${theme.spacing(2)} !important`, sm: `${theme.spacing(2.5)} !important` }
    },
  };

  return sizeConfig[size || 'medium'];
});

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  borderRadius: 'inherit',
  zIndex: 1,
}));

const CardHeaderStyled = styled(CardHeader)<{ size?: CardSize }>(({ theme, size }) => {
  const sizeConfig = {
    small: {
      padding: { xs: theme.spacing(1), sm: theme.spacing(1.25) },
      '& .MuiCardHeader-title': {
        fontSize: { xs: '0.875rem', sm: '1rem' },
        fontWeight: 600,
      },
      '& .MuiCardHeader-subheader': {
        fontSize: { xs: '0.75rem', sm: '0.75rem' },
      },
    },
    medium: {
      padding: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
      '& .MuiCardHeader-title': {
        fontSize: { xs: '1rem', sm: '1.25rem' },
        fontWeight: 600,
      },
      '& .MuiCardHeader-subheader': {
        fontSize: { xs: '0.75rem', sm: '0.875rem' },
      },
    },
    large: {
      padding: { xs: theme.spacing(2), sm: theme.spacing(2.5) },
      '& .MuiCardHeader-title': {
        fontSize: { xs: '1.25rem', sm: '1.5rem' },
        fontWeight: 600,
      },
      '& .MuiCardHeader-subheader': {
        fontSize: { xs: '0.875rem', sm: '1rem' },
      },
    },
  };

  return sizeConfig[size || 'medium'];
});

const CardActionsStyled = styled(CardActions)<{ size?: CardSize }>(({ theme, size }) => {
  const sizeConfig = {
    small: { padding: { xs: theme.spacing(0.5, 0.75), sm: theme.spacing(0.5, 1) } },
    medium: { padding: { xs: theme.spacing(0.75, 1), sm: theme.spacing(1, 1.5) } },
    large: { padding: { xs: theme.spacing(1, 1.5), sm: theme.spacing(1.5, 2) } },
  };

  return sizeConfig[size || 'medium'];
});

export const Card: React.FC<CustomCardProps> = ({
  variant = 'elevated',
  size = 'medium',
  title,
  subtitle,
  avatar,
  headerAction,
  showMenu = false,
  onMenuClick,
  showClose = false,
  onClose,
  actions,
  footer,
  loading = false,
  hover = false,
  clickable = false,
  bordered = false,
  children,
  ...muiProps
}) => {
  const hasHeader = title || subtitle || avatar || headerAction || showMenu || showClose;

  const renderHeaderAction = () => {
    const actionElements: React.ReactNode[] = [];

    if (headerAction) {
      actionElements.push(headerAction);
    }

    if (showMenu && onMenuClick) {
      actionElements.push(
        <IconButton key="menu" size="small" onClick={onMenuClick} aria-label="card menu">
          <MoreVert />
        </IconButton>
      );
    }

    if (showClose && onClose) {
      actionElements.push(
        <IconButton key="close" size="small" onClick={onClose} aria-label="close card">
          <Close />
        </IconButton>
      );
    }

    return actionElements.length > 0 ? (
      <Box sx={{ display: 'flex', gap: 0.5 }}>{actionElements}</Box>
    ) : undefined;
  };

  return (
    <StyledCard
      {...muiProps}
      $variant={variant}
      size={size}
      loading={loading}
      hover={hover}
      clickable={clickable}
      bordered={bordered}
    >
      {loading && (
        <LoadingOverlay>
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </LoadingOverlay>
      )}

      {hasHeader && (
        <>
          <CardHeaderStyled
            size={size}
            avatar={avatar}
            action={renderHeaderAction()}
            title={title}
            subheader={subtitle}
          />
          <Divider />
        </>
      )}

      <StyledCardContent size={size}>{children}</StyledCardContent>

      {actions && (
        <>
          <Divider />
          <CardActionsStyled size={size}>{actions}</CardActionsStyled>
        </>
      )}

      {footer && (
        <>
          <Divider />
          <Box
            sx={{
              p: size === 'small' ? 1 : size === 'large' ? 2 : 1.5,
              backgroundColor: 'grey.50',
            }}
          >
            {footer}
          </Box>
        </>
      )}
    </StyledCard>
  );
};

export default Card;
